import type { ComponentProps } from 'react';
import type { SurveyResponseDto } from '@hotjar/feedback/domain/Surveys/Responses/SurveyResponseDto';
import { ResponseModal } from './ResponseModal';
import type { SurveyQuestion } from './domain/SurveyQuestion';
import { groupAnswersByQuestionId } from './domain/QuestionAnswerMapper';
import { useCurrentResponse } from '../../SurveyView/SurveyResponsesView/useCurrentResponse';

export interface SurveyResponseModalProps
  extends Pick<ComponentProps<typeof ResponseModal>, 'appElementSelector' | 'coveragePercentage'> {
  surveyId: number;
  responseId: number;
  questionsById: Record<string, SurveyQuestion> | null;
  responses: SurveyResponseDto[] | undefined;
  isExternalLinkSurvey: boolean;
  siteId?: number;
}

export function SurveyResponseModal({
  surveyId,
  responseId,
  questionsById,
  responses,
  isExternalLinkSurvey,
  siteId,
  ...props
}: SurveyResponseModalProps): JSX.Element | null {
  const { response, selectResponse, responseIndex } = useCurrentResponse(
    surveyId,
    responseId,
    responses,
  );

  if (!response || !questionsById || !responses || !responses.length) {
    return null;
  }

  const showPreviousButton = responseIndex !== -1 && responseIndex !== 0;
  const showNextButton = responseIndex !== -1 && responseIndex !== responses.length - 1;

  const handleLoadNext = () => {
    selectResponse(responses[responseIndex + 1]?.id);
  };

  const handleLoadPrevious = () => {
    selectResponse(responses[responseIndex - 1]?.id);
  };

  const handleClose = () => {
    selectResponse(null);
  };

  const questionsWithAnswers = groupAnswersByQuestionId(response, questionsById);

  return (
    <ResponseModal
      isOpen
      onClose={handleClose}
      onLoadNext={handleLoadNext}
      onLoadPrevious={handleLoadPrevious}
      questionsWithAnswers={questionsWithAnswers}
      responseIndex={response.index}
      recordingUrl={response.recording_url}
      contentsquareUrl={response.contentsquare_replay_link}
      showNextButton={showNextButton}
      showPreviousButton={showPreviousButton}
      isExternalLinkSurvey={isExternalLinkSurvey}
      siteId={siteId}
      {...props}
    />
  );
}
